/**
 * Auth Query Hook - TanStack Start Compliant
 * React Query wrapper for auth operations using TanStack Start server functions
 *
 * - Server functions for all auth operations
 * - React Query for caching and state management
 * - useServerFn() hook for type-safe RPC calls
 *
 * Use these hooks instead of AuthContext methods for data fetching.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServerFn } from '@tanstack/react-start';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { clearCsrfToken, getCsrfToken, setCsrfToken } from '@/lib/auth/csrfToken';
import { broadcastAuthPurge, purgeAuthState } from '@/lib/auth/authLifecycle';
import { apiClient, type RefreshOutcome, waitForRefreshConflict } from '@/lib/api/client';
import { API_CONFIG } from '@/lib/config/api.config';
import {
  logoutServer,
  type UserSession,
  type TelegramAuthVerifyRequest,
} from '@/lib/server-fns/auth-fns';
import { logger } from '@/lib/utils/logger';

// Dev-only auth bypass for local development. Gated on Vite's build-time
// `DEV` flag (literally `false` in production bundles) so a stray
// VITE_DEV_MODE=true in a prod build config can NEVER enable it.
const DEV_SKIP_AUTH = import.meta.env.DEV && import.meta.env.VITE_DEV_MODE === 'true';

export type AuthUnavailableReason =
  | 'network'
  | 'session_failed'
  | 'session_protocol_error'
  | 'refresh_failed'
  | 'refresh_conflict'
  | 'session_retry_failed';

/**
 * A session could not be verified, but was not proven to be logged out. Route
 * guards must render recovery UI for this error instead of navigating to login.
 */
export class AuthUnavailableError extends Error {
  readonly reason: AuthUnavailableReason;
  readonly status?: number;
  readonly retryable: boolean;

  constructor(reason: AuthUnavailableReason, options: { status?: number; cause?: unknown } = {}) {
    super(`Authentication is temporarily unavailable: ${reason}`, { cause: options.cause });
    this.name = 'AuthUnavailableError';
    this.reason = reason;
    this.status = options.status;
    this.retryable =
      reason !== 'session_protocol_error' &&
      reason !== 'refresh_conflict' &&
      (options.status === undefined || options.status >= 500);
  }
}

export interface SessionRecoveryOptions {
  fetchImpl?: typeof fetch;
  refreshAuth?: () => Promise<RefreshOutcome>;
  sessionUrl?: string;
  signal?: RequestInit['signal'];
  waitForConflict?: (retryAfterMs: number) => Promise<void>;
}

/**
 * Browser fetch reports transport failures (offline, DNS, CORS, refused
 * connection) as a TypeError with unhelpful copy like "Failed to fetch".
 * Real API errors are plain Errors with server messages, so they pass through.
 */
function isFetchNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (error instanceof Error && /Failed to fetch|NetworkError|fetch failed/i.test(error.message))
  );
}

async function readSession(response: Response): Promise<UserSession> {
  try {
    const csrfTokenFromHeader = response.headers.get('X-CSRF-Token');
    if (csrfTokenFromHeader) setCsrfToken(csrfTokenFromHeader);
    const data = (await response.json()) as { user?: UserSession };
    if (!data.user) throw new Error('Session response did not contain a user');
    return data.user;
  } catch (cause) {
    throw new AuthUnavailableError('session_protocol_error', { cause });
  }
}

/**
 * Browser session recovery for an empty React Query cache. A 409 refresh race
 * is not a logout: another tab has likely won token rotation, so wait briefly
 * for its Set-Cookie response and retry the session request exactly once.
 */
export async function fetchSessionWithRecovery(
  options: SessionRecoveryOptions = {}
): Promise<UserSession | null> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const refreshAuth = options.refreshAuth ?? (() => apiClient.refreshAuth());
  const sessionUrl = options.sessionUrl ?? `${API_CONFIG.BASE_URL}/v1/auth/session`;
  const requestSession = () =>
    fetchImpl(sessionUrl, {
      method: 'GET',
      credentials: 'include',
      signal: options.signal,
    });

  try {
    const initialResponse = await requestSession();
    if (initialResponse.ok) return readSession(initialResponse);
    if (initialResponse.status !== 401) {
      throw new AuthUnavailableError('session_failed', { status: initialResponse.status });
    }

    const refreshOutcome = await refreshAuth();
    if (refreshOutcome.status === 'unauthorized') {
      clearCsrfToken();
      return null;
    }
    if (refreshOutcome.status === 'failed') {
      throw new AuthUnavailableError('refresh_failed');
    }
    if (refreshOutcome.status === 'conflict') {
      await (options.waitForConflict ?? waitForRefreshConflict)(refreshOutcome.retryAfterMs);
    }

    const retryResponse = await requestSession();
    if (retryResponse.ok) return readSession(retryResponse);

    // Do not turn a post-conflict/post-refresh 401 into a logout. The winning
    // request may not have reached this cookie jar yet; recovery UI is safer
    // than deleting a potentially valid session.
    throw new AuthUnavailableError(
      refreshOutcome.status === 'conflict' ? 'refresh_conflict' : 'session_retry_failed',
      { status: retryResponse.status }
    );
  } catch (error) {
    if (error instanceof AuthUnavailableError) throw error;
    throw new AuthUnavailableError('network', { cause: error });
  }
}

/**
 * Use Current Session
 * Reads the current user session from React Query cache
 *
 * ✅ SSR MIGRATION: Session Flow:
 * 1. Root loader fetches session server-side
 * 2. RootDocument hydrates session into React Query cache
 * 3. This hook reads from cache (no client-side fetch on mount)
 * 4. httpOnly cookies auto-sent with credentials: "include"
 */
export function useSession() {
  const { authState, setAuthState, setIsInitialized } = useAuth();

  const query = useQuery({
    queryKey: ['auth', 'session'],
    enabled: authState !== 'unavailable',
    queryFn: async () => {
      // Short-circuit in dev skip-auth mode to avoid hitting backend/login
      if (DEV_SKIP_AUTH) {
        logger.info('useSession', 'Dev skip-auth enabled - returning mock user');
        const mockUser: UserSession = {
          id: '00000000-0000-0000-0000-000000000001',
          telegram_id: 123456789,
          telegram_username: 'devuser',
          telegram_first_name: 'Dev',
          telegram_last_name: 'User',
          telegram_photo_url: undefined,
          display_name: 'Dev User',
          profile_picture_url: undefined,
          role: 'dancer',
          created_at: new Date().toISOString(),
          bio: undefined,
        };
        return mockUser;
      }

      // ✅ This queryFn typically won't run because:
      // 1. Root loader fetches session server-side
      // 2. RootDocument hydrates it into cache on mount
      // 3. staleTime: Infinity means it never refetches
      // Only runs if cache is empty (e.g., after logout, explicit invalidation)
      logger.info('useSession', 'Cache miss - fetching session from backend');

      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), API_CONFIG.TIMEOUT_MS);

      try {
        const session = await fetchSessionWithRecovery({ signal: abortController.signal });
        clearTimeout(timeoutId);
        return session;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          logger.error('useSession', `Request timed out after ${API_CONFIG.TIMEOUT_MS}ms`);
        } else {
          logger.error('useSession', error);
        }
        throw error;
      }
    },
    staleTime: Infinity, // ✅ Never refetch - data comes from SSR loader
    gcTime: 1000 * 60 * 30, // 30 minutes
    retry: (failureCount, error) =>
      error instanceof AuthUnavailableError && error.retryable && failureCount < 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    refetchOnWindowFocus: false,
    refetchOnMount: false, // ✅ Critical: Don't refetch on mount, use cached SSR data
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  // Coordinate with AuthContext UI state
  useEffect(() => {
    if (authState === 'unavailable') {
      setIsInitialized(true);
      return;
    }
    if (!query.isLoading) {
      setIsInitialized(true);
      setAuthState(
        query.isError ? 'unavailable' : query.data ? 'authenticated' : 'unauthenticated'
      );
    }
  }, [authState, query.data, query.isError, query.isLoading, setAuthState, setIsInitialized]);

  return query;
}
export function useTelegramAuthInit() {
  return useMutation({
    // ✅ Disable retry for auth mutations - they should only be attempted once
    retry: false,
    mutationFn: async () => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), API_CONFIG.TIMEOUT_MS);

      try {
        // Get CSRF token for this request
        const csrfToken = getCsrfToken();

        // Prepare headers
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        // Add CSRF token if available
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }

        // Use direct browser fetch for auth endpoints so cookies can be set
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/auth/telegram/init`, {
          method: 'POST',
          credentials: 'include', // Critical: Include cookies
          headers,
          signal: abortController.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            error: 'Failed to initialize authentication',
          }));
          throw new Error(errorData.error || 'Failed to initialize authentication');
        }

        const data = await response.json();

        // ✅ Extract and store CSRF token from response (both header and body)
        const csrfTokenFromHeader = response.headers.get('X-CSRF-Token');
        const csrfTokenFromBody = data.csrf_token;
        const responseCsrfToken = csrfTokenFromHeader || csrfTokenFromBody;
        if (responseCsrfToken) {
          setCsrfToken(responseCsrfToken);
          logger.debug('useTelegramAuthInit', 'Stored CSRF token from auth init response');
        }

        return {
          auth_code: data.auth_code,
          auth_url: data.auth_url,
          expires_at: data.expires_at,
          csrf_token: data.csrf_token,
        };
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          logger.error('useTelegramAuthInit', `Request timed out after ${API_CONFIG.TIMEOUT_MS}ms`);
          const timeoutError = new Error(
            'Authentication request timed out. Please check your connection and try again.'
          );
          timeoutError.cause = error;
          throw timeoutError;
        }
        logger.error('useTelegramAuthInit', error);
        if (isFetchNetworkError(error)) {
          throw new Error('Network problem - check your connection and try again.', {
            cause: error,
          });
        }
        throw error;
      }
    },
  });
}

/**
 * Use Telegram Auth Verify
 * Verifies Telegram OAuth callback and creates session
 */
export function useTelegramAuthVerify() {
  const queryClient = useQueryClient();

  return useMutation({
    // ✅ Disable retry for auth mutations - they should only be attempted once
    retry: false,
    mutationFn: async (data: TelegramAuthVerifyRequest) => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), API_CONFIG.TIMEOUT_MS);

      try {
        logger.info('useTelegramAuthVerify', 'Starting verification', {
          authCodeLength: data.auth_code?.length,
          challengeCodeLength: data.challenge_code?.length,
        });

        // Get CSRF token for this request
        const csrfToken = getCsrfToken();

        // Prepare headers
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        // Add CSRF token if available
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }

        // Use direct browser fetch for auth endpoints so cookies can be set
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/auth/telegram/verify`, {
          method: 'POST',
          headers,
          credentials: 'include', // Critical: Include cookies
          signal: abortController.signal,
          body: JSON.stringify({
            auth_code: data.auth_code,
            challenge_code: data.challenge_code,
          }),
        });

        clearTimeout(timeoutId);

        logger.info('useTelegramAuthVerify', 'Response received', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            error: 'Failed to verify authentication',
          }));

          logger.error('useTelegramAuthVerify', 'Verification failed', {
            status: response.status,
            errorData,
            authCodeLength: data.auth_code?.length,
            challengeCodeLength: data.challenge_code?.length,
          });

          throw new Error(errorData.error || 'Failed to verify authentication');
        }

        const result = await response.json();

        // ✅ Extract and store CSRF token from response (both header and body)
        const csrfTokenFromHeader = response.headers.get('X-CSRF-Token');
        const csrfTokenFromBody = result.csrf_token;
        const responseCsrfToken = csrfTokenFromHeader || csrfTokenFromBody;
        if (responseCsrfToken) {
          setCsrfToken(responseCsrfToken);
          logger.debug('useTelegramAuthVerify', 'Stored CSRF token from auth verify response');
        }

        const userSession: UserSession = result.user || result;

        logger.info('useTelegramAuthVerify', 'Verification successful', {
          userId: userSession.id,
        });

        return userSession;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          logger.error(
            'useTelegramAuthVerify',
            `Request timed out after ${API_CONFIG.TIMEOUT_MS}ms`
          );
          const timeoutError = new Error(
            'Verification request timed out. Please check your connection and try again.'
          );
          timeoutError.cause = error;
          throw timeoutError;
        }
        logger.error(
          'useTelegramAuthVerify',
          `Verification error: ${error instanceof Error ? error.message : String(error)}`
        );
        if (isFetchNetworkError(error)) {
          throw new Error('Network problem - check your connection and try again.', {
            cause: error,
          });
        }
        throw error;
      }
    },
    onSuccess: (session: UserSession) => {
      // Update session cache on successful auth
      queryClient.setQueryData<UserSession>(['auth', 'session'], session);
    },
  });
}

/**
 * Use Logout
 * Logs out the current user and cleans up auth state
 *
 * Flow:
 * 1. Call backend /v1/auth/logout (clears refresh_token cookie, revokes session)
 * 2. Clear client-side state (session cache)
 * 3. Redirect to /login handled by caller
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const logoutFn = useServerFn(logoutServer);

  return useMutation({
    mutationFn: async () => {
      try {
        logger.info('useLogout', 'Starting logout process');
        return await logoutFn();
      } catch (error) {
        logger.error('useLogout', 'Logout API call failed', { error });
        // Don't throw - we'll clean up client state anyway
        // Backend might be unreachable, but we can still log out locally
        return { success: false };
      }
    },
    onSettled: () => {
      // Always clean up client state, even if API call failed
      logger.info('useLogout', 'Cleaning up client-side auth state');

      // Purge ALL cached queries + CSRF state, then tell sibling tabs to do
      // the same (#866). On a shared device this prevents the next user from
      // seeing the previous user's private data (favorites, profile, settings,
      // market purchases, community like-state, account info) that would
      // otherwise survive in cache for up to gcTime (30m). Enumerating
      // user-scoped keys is brittle (a new query silently re-introduces the
      // leak); a full clear is bulletproof and public queries simply refetch.
      // purgeAuthState also marks the session null so useIsAuthenticated()
      // flips false immediately and the guard redirects.
      purgeAuthState(queryClient);
      broadcastAuthPurge();

      // Clear dev token if in dev mode
      if (typeof window !== 'undefined') {
        const isDev =
          window.location.hostname === 'app.til.test' ||
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1';
        if (isDev) {
          localStorage.removeItem('dev_access_token');
        }
      }

      logger.info('useLogout', 'Logout complete');
    },
  });
}

/**
 * Helper hook to check authentication status
 */
export function useIsAuthenticated() {
  const { data: session, isLoading, isError } = useSession();

  return {
    isAuthenticated: !!session,
    isLoading,
    isError,
    user: session,
  };
}

/**
 * Admin Login Mutation
 * Login with email and password (dev/staging only)
 *
 * ENVIRONMENT CHECK: Backend rejects if Environment == "production"
 */
export function useAdminLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      logger.info('useAdminLogin', 'Attempting admin password login', {
        email: credentials.email,
      });

      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), API_CONFIG.TIMEOUT_MS);

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/auth/admin/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          signal: abortController.signal,
          body: JSON.stringify(credentials),
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || `Login failed: ${response.statusText}`);
        }

        const data = await response.json();
        logger.info('useAdminLogin', 'Admin login successful', {
          userId: data.user?.id,
          role: data.user?.role,
        });

        return data.user as UserSession;
      } catch (error) {
        clearTimeout(timeoutId);
        if (isFetchNetworkError(error)) {
          throw new Error('Network problem - check your connection and try again.', {
            cause: error,
          });
        }
        throw error;
      }
    },
    onSuccess: (session: UserSession) => {
      // Update session cache
      queryClient.setQueryData(['auth', 'session'], session);
      logger.info('useAdminLogin', 'Session cache updated', {
        userId: session.id,
        role: session.role,
      });
    },
  });
}
